import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mic, Square, X, Sparkles, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import VoiceConsentModal, { hasVoiceConsent } from "@/components/VoiceConsentModal";

/**
 * VoiceNoteRecorder. modal-based recorder for evidence-grade voice notes.
 *
 * UX flow:
 *   1. Consent gate (shown once per browser).
 *   2. Live timer + animated mic-level bars (no actual waveform — simple is robust).
 *   3. Auto-stops at the user's tier cap. Server double-checks duration.
 *   4. After stop: preview audio + label field, then Save (no transcribe) or
 *      Save + Transcribe. Each option calls /api/cases/{id}/voice-notes.
 *
 * Props:
 *   caseId          required. case the note attaches to
 *   onUploaded(doc) callback fired after successful upload
 *   onClose         close handler
 *   defaultLabel    optional pre-filled label (e.g. "Inspection 14 Mar")
 */
export default function VoiceNoteRecorder({ caseId, onUploaded, onClose, defaultLabel = "" }) {
  const { user, refreshUser } = useAuth();
  const [consentOpen, setConsentOpen] = useState(!hasVoiceConsent());
  const [phase, setPhase] = useState("idle"); // idle | recording | recorded | uploading
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [label, setLabel] = useState(defaultLabel);
  const [levels, setLevels] = useState([0, 0, 0, 0, 0]);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [geo, setGeo] = useState(null); // { lat, lng, accuracy }
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | locating | ok | denied

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);

  const maxSeconds = user?.voice_max_seconds || 180;
  const transcribesLeft = Math.max(0, (user?.voice_transcribes_daily_limit || 0) - (user?.voice_transcribes_used_today || 0));
  const canTranscribe = transcribesLeft > 0;

  // Cleanup on unmount
  useEffect(() => {
    return () => stopEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Emergency mode: once we have the recorded blob, auto-fire the upload
  // with transcription + share link. Removes 2 taps from the panic flow.
  useEffect(() => {
    if (phase === "recorded" && emergencyMode && audioBlob) {
      upload(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, emergencyMode, audioBlob]);

  const stopEverything = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Optional geolocation grab — fires in parallel, never blocks recording.
      if (navigator.geolocation && geoStatus === "idle") {
        setGeoStatus("locating");
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setGeo({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            });
            setGeoStatus("ok");
          },
          () => setGeoStatus("denied"),
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
        );
      }

      // Mic-level meter
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ac = new Ctx();
      audioCtxRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const an = ac.createAnalyser();
      an.fftSize = 64;
      src.connect(an);
      analyserRef.current = an;
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(buf);
        const chunk = Math.floor(buf.length / 5);
        const next = [];
        for (let i = 0; i < 5; i++) {
          let sum = 0;
          for (let j = 0; j < chunk; j++) sum += buf[i * chunk + j];
          next.push(Math.min(1, sum / (chunk * 255)));
        }
        setLevels(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setPhase("recorded");
        stopEverything();
      };
      mr.start();
      mediaRef.current = mr;
      setPhase("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= maxSeconds) {
            // Auto-stop at tier cap
            try { mr.stop(); } catch (e) { /* recorder may already be stopped */ }
            toast.info(`Reached ${Math.floor(maxSeconds / 60)} min limit on your plan. Saved.`);
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone access denied. Allow mic access in your browser, then try again.");
    }
  };

  const stop = () => { try { mediaRef.current?.stop(); } catch (e) { /* ignore */ } };

  const discard = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null); setAudioUrl(null);
    setSeconds(0); setPhase("idle");
  };

  const upload = async (autoTranscribe) => {
    if (!audioBlob) return;
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", new File([audioBlob], `voice-${Date.now()}.webm`, { type: "audio/webm" }));
      fd.append("duration_seconds", String(seconds));
      fd.append("auto_transcribe", autoTranscribe ? "true" : "false");
      if (label.trim()) fd.append("title", label.trim());
      if (geo) {
        fd.append("geo_lat", String(geo.lat));
        fd.append("geo_lng", String(geo.lng));
        if (geo.accuracy) fd.append("geo_accuracy_m", String(geo.accuracy));
      }
      const { data } = await api.post(`/cases/${caseId}/voice-notes`, fd);
      let shareUrl = null;
      // Emergency mode: immediately generate a share link + copy to clipboard
      // so the user can paste into SMS / Messenger without extra taps.
      if (emergencyMode) {
        try {
          const { data: share } = await api.post(`/voice-notes/${data.id}/share`);
          shareUrl = share.public_url || share.share_url;
          if (shareUrl && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(shareUrl).catch(() => {});
          }
        } catch (e) { /* share fail is non-fatal */ }
      }
      onUploaded?.(data);
      await refreshUser();
      if (shareUrl) {
        toast.success("Link copied. paste into SMS, WhatsApp or Messenger to share.", { duration: 8000 });
      } else {
        toast.success(
          autoTranscribe
            ? (data.transcript_status === "completed" ? "Voice note saved and transcribed." : "Voice note saved. transcript pending.")
            : "Voice note saved to your case."
        );
      }
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Voice upload failed.";
      toast.error(detail);
      setPhase("recorded");
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  if (consentOpen) {
    return <VoiceConsentModal onAccept={() => setConsentOpen(false)} onClose={onClose} />;
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={phase === "idle" ? onClose : undefined}
      data-testid="voice-note-recorder"
    >
      <div
        className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-2">
            <Mic size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
            <h3 className="font-serif text-xl text-[#0D2B52]">Voice note</h3>
          </div>
          <button
            onClick={onClose}
            disabled={phase === "recording" || phase === "uploading"}
            className="btn-ghost p-1.5 disabled:opacity-30"
            aria-label="Close"
            data-testid="voice-recorder-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6">
          {/* Timer + level meter */}
          <div className="text-center mb-6">
            <div className="font-mono text-4xl text-[#0D2B52] tabular-nums" data-testid="voice-timer">
              {fmt(seconds)}
              <span className="text-sm text-[#6B7280] ml-2">/ {fmt(maxSeconds)}</span>
            </div>
            <div className="flex justify-center items-end gap-1.5 h-12 mt-3">
              {levels.map((l, i) => (
                <div
                  key={i}
                  className={`w-2 rounded-sm transition-all duration-75 ${phase === "recording" ? "bg-[#B35D4C]" : "bg-[#D6D5CE]"}`}
                  style={{ height: `${Math.max(8, l * 48)}px` }}
                />
              ))}
            </div>
            {phase === "recording" && (
              <p className="text-[11px] font-mono text-[#B35D4C] uppercase tracking-wider mt-2">Recording</p>
            )}
          </div>

          {/* Controls */}
          {phase === "idle" && (
            <div className="space-y-3">
              <button
                onClick={start}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3"
                data-testid="voice-recorder-start"
              >
                <Mic size={16} strokeWidth={1.5} /> Start recording
              </button>
              <label className="flex items-start gap-2 cursor-pointer bg-[#FFF4EC] border border-[#F0CDB8] p-3 rounded-sm">
                <input
                  type="checkbox"
                  checked={emergencyMode}
                  onChange={(e) => setEmergencyMode(e.target.checked)}
                  className="mt-0.5"
                  data-testid="voice-recorder-emergency-toggle"
                />
                <span className="text-xs text-[#333] leading-relaxed">
                  <span className="font-semibold text-[#B35D4C]">Quick share mode.</span>
                  {" "}On finish, auto-transcribe and put a share link in your clipboard. paste into SMS, WhatsApp or Messenger yourself. Adds your location if you allow it.
                </span>
              </label>
              <p className="text-xs text-[#6B7280] text-center">
                Up to {Math.floor(maxSeconds / 60)} min per recording on your <span className="font-mono">{user?.tier}</span> plan.
              </p>
            </div>
          )}

          {phase === "recording" && (
            <button
              onClick={stop}
              className="w-full inline-flex items-center justify-center gap-2 py-3 bg-[#B35D4C] text-white hover:bg-[#9c4d3e] rounded-sm font-serif"
              data-testid="voice-recorder-stop"
            >
              <Square size={14} strokeWidth={2} fill="currentColor" /> Stop recording
            </button>
          )}

          {phase === "recorded" && audioUrl && (
            <div className="space-y-3">
              <audio
                controls
                src={audioUrl}
                className="w-full"
                data-testid="voice-recorder-preview"
              />
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. Inspection 14 Mar, Agent visit, Landlord call)"
                className="input-field w-full text-sm"
                maxLength={120}
                data-testid="voice-recorder-label"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => upload(false)}
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-sm py-2.5"
                  data-testid="voice-recorder-save"
                >
                  <Save size={14} strokeWidth={1.5} /> Save audio only
                </button>
                <button
                  onClick={() => upload(true)}
                  disabled={!canTranscribe}
                  className="btn-primary inline-flex items-center justify-center gap-2 text-sm py-2.5 disabled:opacity-40"
                  data-testid="voice-recorder-save-transcribe"
                  title={canTranscribe ? "" : "Daily transcription limit reached for your plan"}
                >
                  <Sparkles size={14} strokeWidth={1.5} /> Save + transcribe
                </button>
              </div>
              <div className="flex justify-between items-center text-[11px] text-[#6B7280]">
                <button
                  onClick={discard}
                  className="inline-flex items-center gap-1 hover:text-[#B35D4C]"
                  data-testid="voice-recorder-discard"
                >
                  <Trash2 size={11} strokeWidth={1.5} /> Discard
                </button>
                <span className="font-mono">
                  {transcribesLeft} / {user?.voice_transcribes_daily_limit || 0} transcriptions left today
                </span>
              </div>
            </div>
          )}

          {phase === "uploading" && (
            <div className="text-center py-8" data-testid="voice-recorder-uploading">
              <Loader2 size={28} className="mx-auto animate-spin text-[#1B6C62]" />
              <p className="text-sm text-[#6B7280] mt-3">Saving your voice note…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
